import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from '../components/FAQAccordion';
import { FAQSchema, BreadcrumbSchema } from '../components/SchemaMarkup';
import { services, faqData, cities, businessData } from '../data/seoData';

const FAQPage = () => {
  // Kategorisierte FAQs
  const categories = [
    {
      name: 'Nach dem Unfall',
      faqs: faqData.filter(f => [1, 2, 4, 5, 11].includes(f.id))
    },
    {
      name: 'Kosten & Versicherung',
      faqs: faqData.filter(f => [3, 7, 12].includes(f.id))
    },
    {
      name: 'Gutachten Inhalte',
      faqs: faqData.filter(f => [8, 9, 10, 13, 14, 18].includes(f.id))
    },
    {
      name: 'Ablauf & Dauer',
      faqs: faqData.filter(f => [6, 15, 17].includes(f.id))
    },
    {
      name: 'Kontakt & Region',
      faqs: faqData.filter(f => [16, 19, 20].includes(f.id))
    }
  ];

  return (
    <div style={{ background: '#FFFFFF', position: 'relative' }}>
      <Helmet>
        <title>Häufig gestellte Fragen | Crash Check Kfz Gutachter</title>
        <meta name="description" content="Alle Antworten zu Unfallgutachten, Schadengutachten, Wertminderung und Nutzungsausfall. FAQ für Kfz Gutachten im Rhein-Main-Gebiet." />
        <link rel="canonical" href={`${businessData.url}/faq`} />
      </Helmet>

      <FAQSchema faqs={faqData} />
      <BreadcrumbSchema items={[
        { name: 'Start', url: '/' },
        { name: 'FAQ', url: '/faq' }
      ]} />

      <section style={{ paddingTop: '140px', paddingBottom: '80px', background: 'linear-gradient(180deg, #F0F4F8 0%, #FFFFFF 100%)' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1000px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h1 style={{
              fontSize: 'clamp(36px, 6vw, 56px)',
              fontWeight: '700',
              lineHeight: '1.2',
              color: '#1a202c',
              marginBottom: '24px'
            }}>
              Häufig gestellte Fragen
            </h1>
            <p style={{
              fontSize: '20px',
              lineHeight: '1.7',
              color: '#4a5568'
            }}>
              Alle wichtigen Antworten zu Kfz Gutachten, Unfällen und Schadensdokumentation
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      {categories.map((category, catIndex) => (
        <section key={catIndex} className="pad-large" style={{ background: catIndex % 2 === 0 ? '#FFFFFF' : '#F8F9FA' }}>
          <div className="container mx-auto px-4" style={{ maxWidth: '900px' }}>
            <h2 style={{
              fontSize: 'clamp(24px, 4vw, 32px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '32px',
              paddingBottom: '16px',
              borderBottom: '2px solid #E2E8F0'
            }}>
              {category.name}
            </h2>
            <FAQAccordion faqs={category.faqs} />
          </div>
        </section>
      ))}

      {/* Quick Links zu Leistungen */}
      <section className="pad-large" style={{ background: '#F0F4F8' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '16px'
            }}>
              Unsere Leistungen
            </h2>
            <p style={{ fontSize: '18px', color: '#4a5568' }}>
              Mehr Details zu unseren Gutachten-Leistungen
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.values(services).map((service, index) => (
              <Link
                key={index}
                to={service.url}
                className="glass-card"
                style={{
                  padding: '24px',
                  textDecoration: 'none',
                  display: 'block',
                  transition: 'all 0.3s ease'
                }}
              >
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1a202c',
                  marginBottom: '8px'
                }}>
                  {service.name}
                </h3>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#0891B2',
                  fontSize: '15px',
                  fontWeight: '600'
                }}>
                  Mehr erfahren
                  <ArrowRight size={16} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links zu Städten */}
      <section className="pad-large" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1000px' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '16px'
            }}>
              Wir sind vor Ort im Rhein-Main-Gebiet
            </h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
            {Object.values(cities).map((city) => (
              <Link
                key={city.slug}
                to={city.url}
                className="glass-card"
                style={{
                  padding: '16px 24px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(8, 145, 178, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#1a202c' }}>
                  {city.name}
                </span>
                <ArrowRight size={16} style={{ color: '#0891B2' }} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pad-large" style={{ background: '#F0F4F8' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '900px' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.1) 0%, rgba(6, 182, 212, 0.05) 100%)',
            border: '1px solid rgba(8, 145, 178, 0.2)',
            borderRadius: '24px',
            padding: '60px 40px',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px'
            }}>
              Weitere Fragen?<br/>Wir helfen gerne weiter
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#4a5568',
              lineHeight: '1.7',
              marginBottom: '40px',
              maxWidth: '600px',
              margin: '0 auto 40px'
            }}>
              Kontaktieren Sie uns per WhatsApp, Telefon oder Formular.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary" style={{ minHeight: '56px' }}>
                Kontakt aufnehmen
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
